import React, { useCallback, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import VideoEditingService, {
  ClipCategory, ClipLabel, ClipLabelVocabulary, LibraryClipLabel, NewClipLabel, VideoClip,
} from 'services/VideoEditingService';
import CategoryPicker from 'pages/VideoEditing/components/CategoryPicker';
import { roundSec } from 'pages/VideoEditing/components/ClipTrimmer';
import { useClipPreviewStatus } from 'pages/VideoEditing/useClipPreviewStatus';
import AddClipCard from './AddClipCard';
import AddClipDialog from './AddClipDialog';
import { humanize } from './BrollFilterBar';
import { BULK_ADD_CONCURRENCY, BulkAddFailure, runBulkAdd } from './bulkAdd';

const videoEditingService = new VideoEditingService();

// Past this many clips in one go, the run is long enough that a person should have chosen it
// rather than drifted into it — each add is a download plus two ffmpeg passes server-side.
const LARGE_BATCH = 50;

// The most clips this tab will pull from one folder.
//
// Not a display nicety: every video in a listing is queued for a transcode AND a whisper
// transcript, so pointing at a folder of hundreds spends real money and worker hours on
// footage nobody was going to scroll to. A folder over this says so and asks you to narrow
// it, which for Dropbox usually means opening one of its subfolders.
const CLIP_LIMIT = 25;
// How many failures to name before summarising the rest.
const FAILURES_SHOWN = 5;

interface AddClipsTabProps {
  vocabulary: ClipLabelVocabulary | null;
  categories: ClipCategory[] | null;
  onCreateCategory: (name: string) => Promise<ClipCategory>;
  // Fired after any successful add, so the Library tab can re-search and re-count. The product
  // comes back with it so the page can offer to jump to where the new clips actually are, and
  // `heldBack` is how many of them the server judged too loud for the reusable-b-roll view.
  onAdded: (added: number, productCategory: string | null, heldBack: number) => void;
}

/**
 * Point the library at a folder of footage — Google Drive or Dropbox — and file clips out of it.
 *
 * Before this, the only ways into the library were the CLI labeler and a detour through a
 * Video Editing project — you had to open a project you didn't want in order to add b-roll.
 * This loads a folder exactly as the editor does (`GET /clips`, which also creates the clip
 * index rows and queues the transcodes) and then offers each clip two ways in: one at a time
 * with a trimmer, or in bulk as whole takes under one product.
 *
 * One box takes either provider rather than a Drive/Dropbox toggle: people paste a link they
 * were sent, and asking them to first classify it is a question the backend can answer itself.
 * Dropbox files are NOT copied anywhere — they stay in Dropbox and stream through the same
 * proxy, so from here on a Dropbox clip is indistinguishable from a Drive one.
 */
export default function AddClipsTab({
  vocabulary, categories, onCreateCategory, onAdded,
}: AddClipsTabProps) {
  const [sourceUrl, setSourceUrl] = useState('');
  // Only revealed once Dropbox says a link needs one, so the field never appears for the
  // Drive links that are most of the traffic.
  const [password, setPassword] = useState('');
  const [needsPassword, setNeedsPassword] = useState(false);
  const [folderId, setFolderId] = useState<string | null>(null);
  const [clips, setClips] = useState<VideoClip[] | null>(null);
  // The folder held more than CLIP_LIMIT — shown prominently, because a silently capped grid
  // looks exactly like a folder with fewer clips in it.
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // fileId -> how many library entries it already has, so the grid can say what's in already.
  const [existingCounts, setExistingCounts] = useState<Record<string, number>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState<VideoClip | null>(null);

  // The bulk draft, applied to every clip in the run. Product is optional but strongly worth
  // setting — see the note under the bar.
  const [bulkProduct, setBulkProduct] = useState<string | null>(null);
  const [bulkCategories, setBulkCategories] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failures, setFailures] = useState<BulkAddFailure<VideoClip>[]>([]);
  // A ref, not state: the pool reads it between items and must see the current value without
  // waiting for a re-render.
  const stopRef = useRef(false);

  const videoClips = useMemo(
    () => (clips || []).filter(c => c.mimeType.startsWith('video/')),
    [clips],
  );
  const audioCount = (clips?.length || 0) - videoClips.length;

  const {
    statuses, errors: previewErrors, retry: retryPreview,
  } = useClipPreviewStatus(videoClips);

  // A clip can be added once its preview exists and we know how long it is. Both are hard
  // requirements, not presentation: without a preview the stream proxy has nothing to serve,
  // and without a duration there is no end point to send.
  const isAddable = useCallback(
    (clip: VideoClip) => statuses[clip.fileId] === 'ready' && (clip.durationSec ?? 0) > 0.2,
    [statuses],
  );

  const productOptions = useMemo(
    () => vocabulary?.dimensions.find(d => d.key === 'productCategory')?.values.map(v => v.value) || [],
    [vocabulary],
  );

  async function loadFolder(refresh = false) {
    const trimmed = sourceUrl.trim();
    if (!trimmed && !folderId) return;
    setLoading(true);
    setError(null);
    setFailures([]);
    try {
      const resp = await videoEditingService.listClips(
        refresh && folderId
          ? { folderId, refresh: true, limit: CLIP_LIMIT }
          : { sourceUrl: trimmed, limit: CLIP_LIMIT, ...(password ? { password } : {}) },
      );
      setClips(resp.clips);
      setTruncated(!!resp.truncated);
      setFolderId(resp.folderId || null);
      setNeedsPassword(false);
      setSelected(new Set());
      // Straight after the listing and BEFORE the first preview-status poll. That poll inserts
      // clip-index rows with no folder on them, and this read filters by folder — asking first
      // is what keeps the "already in library" badges from under-reporting.
      if (resp.folderId) await loadExistingLabels(resp.folderId);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Could not load that folder';
      // A password-protected Dropbox link is the one failure with an obvious next step, so
      // reveal the field rather than leaving the person to guess at the message.
      if (/password/i.test(message)) setNeedsPassword(true);
      setError(message);
      setClips(null);
      setTruncated(false);
    } finally {
      setLoading(false);
    }
  }

  async function loadExistingLabels(id: string) {
    const resp = await videoEditingService.listClipLabels(id);
    const counts: Record<string, number> = {};
    for (const label of (resp?.labels || []) as ClipLabel[]) {
      counts[label.fileId] = (counts[label.fileId] || 0) + 1;
    }
    setExistingCounts(counts);
  }

  const noteAdded = useCallback((label: { fileId: string }) => {
    setExistingCounts(current => ({ ...current, [label.fileId]: (current[label.fileId] || 0) + 1 }));
  }, []);

  // Whether the server judged this clip unusable as reusable b-roll. Read off the response
  // rather than re-deriving it here: the sound threshold is the backend's rule, and a second
  // copy of it in the dashboard would drift the moment one side moved.
  const heldBackFrom = (label: LibraryClipLabel) => ((label.brollDisqualifiers || []).length ? 1 : 0);

  async function addOne(input: NewClipLabel) {
    const { label } = await videoEditingService.createClipLabel(input);
    noteAdded(label);
    onAdded(1, label.productCategory, heldBackFrom(label));
  }

  function toggle(fileId: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }

  // Everything addable that isn't in the library yet. Already-added clips are skipped rather
  // than blocked — a second range of the same take is legitimate — but they shouldn't be swept
  // up by a button that says "all".
  function selectAllAddable() {
    setSelected(new Set(
      videoClips.filter(c => isAddable(c) && !existingCounts[c.fileId]).map(c => c.fileId),
    ));
  }

  const selectedClips = useMemo(
    () => videoClips.filter(c => selected.has(c.fileId) && isAddable(c)),
    [videoClips, selected, isAddable],
  );
  const alreadyInLibrary = selectedClips.filter(c => existingCounts[c.fileId]).length;

  async function runBulk() {
    setConfirming(false);
    stopRef.current = false;
    setFailures([]);
    setProgress({ done: 0, total: selectedClips.length });
    let heldBack = 0;
    const { succeeded, failed } = await runBulkAdd(
      selectedClips,
      async (clip) => {
        const { label } = await videoEditingService.createClipLabel({
          fileId: clip.fileId,
          inSec: 0,
          outSec: roundSec(clip.durationSec as number),
          creationSource: 'library_import',
          productCategory: bulkProduct,
          ...(categories ? { categories: bulkCategories } : {}),
        });
        noteAdded(label);
        heldBack += heldBackFrom(label);
      },
      {
        onProgress: (done, total) => setProgress({ done, total }),
        shouldStop: () => stopRef.current,
      },
    );
    setProgress(null);
    setFailures(failed);
    // Only the ones that failed stay ticked, so pressing the button again is exactly a retry.
    setSelected(new Set(failed.map(f => f.item.fileId)));
    if (succeeded.length) onAdded(succeeded.length, bulkProduct, heldBack);
  }

  const readyCount = videoClips.filter(c => statuses[c.fileId] === 'ready').length;
  const preparing = videoClips.length - readyCount;

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField
            fullWidth
            size="small"
            label="Drive or Dropbox folder link"
            placeholder="https://drive.google.com/drive/folders/… or https://www.dropbox.com/scl/fo/…"
            value={sourceUrl}
            onChange={e => setSourceUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') loadFolder(); }}
            disabled={loading}
          />
          <Button
            variant="contained"
            onClick={() => loadFolder()}
            disabled={loading || !sourceUrl.trim()}
            sx={{ flexShrink: 0 }}
          >
            Load clips
          </Button>
          {folderId && (
            <Button
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={() => loadFolder(true)}
              disabled={loading}
              sx={{ flexShrink: 0 }}
            >
              Refresh
            </Button>
          )}
        </Box>
        {needsPassword && (
          <TextField
            fullWidth
            size="small"
            type="password"
            label="Link password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') loadFolder(); }}
            disabled={loading}
            sx={{ mt: 1.5 }}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          A Drive folder has to be shared with the service account; a Dropbox link has to be
          set to anyone with the link. Dropbox files stay in Dropbox — nothing is copied.
          Clips are prepared for preview as soon as they load, and can be added once that
          finishes.
        </Typography>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {failures.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setFailures([])}>
          <AlertTitle>{failures.length} clip{failures.length === 1 ? '' : 's'} could not be added</AlertTitle>
          {failures.slice(0, FAILURES_SHOWN).map(f => (
            <Typography key={f.item.fileId} variant="caption" sx={{ display: 'block' }}>
              {f.item.name}: {f.message}
            </Typography>
          ))}
          {failures.length > FAILURES_SHOWN && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              …and {failures.length - FAILURES_SHOWN} more.
            </Typography>
          )}
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
            They’re still selected, so “Add selected” will try them again.
          </Typography>
        </Alert>
      )}

      {clips && clips.length === 0 && (
        <Alert severity="info">That folder has no video or audio files in it.</Alert>
      )}

      {truncated && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>That folder holds more than {CLIP_LIMIT} clips</AlertTitle>
          Showing the first {CLIP_LIMIT}. Point at a subfolder to see the rest — every clip
          loaded here is queued for a preview and a transcript, so whole folders of footage
          are worth narrowing down first.
        </Alert>
      )}

      {videoClips.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <Autocomplete
                sx={{ minWidth: 220 }}
                size="small"
                options={productOptions}
                getOptionLabel={humanize}
                value={bulkProduct}
                onChange={(_, next) => setBulkProduct(next)}
                disabled={!!progress}
                renderInput={params => (
                  <TextField {...params} label="Product (optional)" placeholder="which product" />
                )}
              />
              {categories && (
                <Box sx={{ minWidth: 260 }}>
                  <CategoryPicker
                    categories={categories}
                    value={bulkCategories}
                    onChange={setBulkCategories}
                    onCreate={onCreateCategory}
                    size="small"
                    disabled={!!progress}
                  />
                </Box>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Button size="small" onClick={selectAllAddable} disabled={!!progress}>
                Select all ready
              </Button>
              {/* No longer gated on a product. The library searches fine without one, so
                  refusing to add would be friction defending a rule that no longer exists —
                  a product is worth setting, not worth blocking on. */}
              <Button
                variant="contained"
                onClick={() => setConfirming(true)}
                disabled={!!progress || selectedClips.length === 0}
              >
                Add {selectedClips.length || ''} selected
              </Button>
            </Box>
            {!bulkProduct && selectedClips.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                No product set — these will still be searchable, but filing them under one makes
                them much easier to find later.
              </Typography>
            )}
            {progress && (
              <Box sx={{ mt: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                    Adding {progress.done} of {progress.total}…
                  </Typography>
                  <Button size="small" color="error" onClick={() => { stopRef.current = true; }}>
                    Stop
                  </Button>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress.total ? (progress.done / progress.total) * 100 : 0}
                />
              </Box>
            )}
          </Paper>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {videoClips.length} clip{videoClips.length === 1 ? '' : 's'}
            {preparing > 0 ? ` · preparing ${preparing}` : ''}
            {/* Audio never gets a clip-index row, so it could never hold a library entry.
                Said out loud so the grid doesn't look like it dropped files. */}
            {audioCount > 0 ? ` · ${audioCount} audio file${audioCount === 1 ? '' : 's'} hidden` : ''}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(6, 1fr)',
              },
            }}
          >
            {videoClips.map(clip => (
              <AddClipCard
                key={clip.fileId}
                clip={clip}
                status={statuses[clip.fileId]}
                previewError={previewErrors[clip.fileId]}
                existingCount={existingCounts[clip.fileId] || 0}
                selected={selected.has(clip.fileId)}
                selectable={isAddable(clip)}
                onToggle={toggle}
                onAdd={setAdding}
                onRetryPreview={retryPreview}
              />
            ))}
          </Box>
        </>
      )}

      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add {selectedClips.length} clip{selectedClips.length === 1 ? '' : 's'}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Each one goes in whole, filed under {bulkProduct ? humanize(bulkProduct) : 'no product'}.
            Trim one instead by pressing “Add…” on its card.
          </Typography>
          {alreadyInLibrary > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {alreadyInLibrary} of them already {alreadyInLibrary === 1 ? 'has an entry' : 'have entries'} in
              the library — this adds another one each.
            </Typography>
          )}
          {selectedClips.length > LARGE_BATCH && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              That’s a lot at once. They go {BULK_ADD_CONCURRENCY} at a time and each takes a
              few seconds, so expect this to run for a while. You can stop it partway.
            </Typography>
          )}
          {/* The one consequence nothing else on the page would tell you about. */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Adding a clip by hand marks it as yours, so the AI labeler will leave that file
            alone from now on.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button variant="contained" onClick={runBulk}>Add them</Button>
        </DialogActions>
      </Dialog>

      {adding && (
        <AddClipDialog
          clip={adding}
          vocabulary={vocabulary}
          categories={categories}
          onCreateCategory={onCreateCategory}
          onClose={() => setAdding(null)}
          onSave={addOne}
        />
      )}
    </Box>
  );
}
